import { useNavigate } from "react-router";
import { Fragment, SyntheticEvent, useMemo, useState } from "react";
import SortableTableHead, { HeadCell } from "components/Common/Table/SortableTableHead.tsx";
import {
	SearchStoresInputText,
	StoreSearchNoResultsFound,
	StoreTableLeadAssignmentLabelText,
	StoreTableMapVisibilityLabelText,
	StoreTableNameLabelText,
	StoreTableStoreAddressLabelText
} from "components/Preferences/text.ts";
import { useHandleTableSort } from "hooks/useHandleTableSort.ts";
import {
	AdminSearchCheckYourSpellingBullet,
	AdminSearchNoResultsFoundSubheader,
	AdminStoreSearchUserBullet,
	ContactCareForSearchHelpText,
	UnavailableDataPlaceholderText
} from "constants/text.ts";
import { StyledTableCell, StyledTableWrapper } from "components/Common/Table/StyledTable.styles.ts";
import ChevronRight from "@mui/icons-material/ChevronRight";
import TableSkeleton from "components/Common/Table/TableSkeleton/TableSkeleton.tsx";
import SuggestedSearch from "components/Common/SuggestedSearch/SuggestedSearch.tsx";
import { TableBody } from "@mui/material";
import { formatAddress } from "../../../utils/address.ts";
import * as Styles from "./StoresSettingsTable.styles.ts";
import { LeadAssignmentPreference } from "types/declarations/enums.ts";
import { models } from "types/api/viewModels.ts";

interface StoresSettingsTableProps {
	stores?: models["StoreSettingsPageViewModel"][] | null;
	isLoading: boolean;
}

const StoresSettingsTable = ({ stores, isLoading }: StoresSettingsTableProps) => {
	const navigate = useNavigate();
	const [searchValue, setSearchValue] = useState<string | null>(null);

	const normalizeText = (value: string | null | undefined) => (value ?? "").toLowerCase();

	const getStoreNames = (storeList: models["StoreSettingsPageViewModel"][] | undefined | null) => {
		return storeList
			?.map((store) => {
				return store.storeName;
			})
			.filter((name): name is string => name != null);
	};

	const searchOptions = useMemo(() => {
		return getStoreNames(stores) ?? [];
	}, [stores]);

	const baseTableHeadCells = useMemo(
		() =>
			[
				{ id: "storeName", label: StoreTableNameLabelText, sortable: true },
				{ id: "mapVisibility", label: StoreTableMapVisibilityLabelText },
				{ id: "leadAssignment", label: StoreTableLeadAssignmentLabelText },
				{ id: "storeAddress", label: StoreTableStoreAddressLabelText },
				{ id: "open", label: "" }
			].map((cell) => ({
				...cell,
				disablePadding: false,
				numeric: false,
				sortable: cell.sortable ?? false
			})) satisfies HeadCell[],
		[]
	);
	const filteredHeadCells = useMemo(() => baseTableHeadCells, [baseTableHeadCells]);

	const { handleRequestSort, order, orderBy } = useHandleTableSort("storeName", "asc");

	const getLocatorEnabledText = (enabled: boolean | null | undefined): string => {
		if (enabled == null) return UnavailableDataPlaceholderText;
		return enabled ? "Show on Dealer Locator Map" : "Do Not Show on Dealer Locator Map";
	};

	const getLeadAssignmentText = (preference: string | null | undefined): string => {
		if (!preference) return UnavailableDataPlaceholderText;
		if (preference === LeadAssignmentPreference.ByAdminsOnly) return "By Admins Only";
		if (preference === LeadAssignmentPreference.ByAnyone) return "By Anyone";
		return preference;
	};

	const getTableCells = (store: models["StoreSettingsPageViewModel"]) => {
		const tableCells = [
			{
				testId: "store-settings-table-name",
				props: { fixedWidth: "250px" },
				content: store?.storeName ?? UnavailableDataPlaceholderText
			},
			{
				testId: "store-settings-table-map-visibility",
				props: { fixedWidth: "250px" },
				content: getLocatorEnabledText(store?.offerings?.locatorEnabled)
			},
			{
				testId: "store-settings-table-pricing-lead-assignment",
				props: { fixedWidth: "20px" },
				content: getLeadAssignmentText(store?.settings?.leadAssignmentPreference)
			},
			{
				testId: "store-settings-table-pricing-store-address",
				props: { fixedWidth: "20px" },
				content: formatAddress(store.storeInfo) ?? UnavailableDataPlaceholderText
			}
		];

		return (
			<Fragment key={store.storeId}>
				{tableCells.map(({ testId, props, content }) => (
					<StyledTableCell
						key={testId}
						data-testid={testId}
						{...props}
					>
						{content}
					</StyledTableCell>
				))}

				<StyledTableCell
					fixedWidth="40px"
					isLastIndex
				>
					<ChevronRight />
				</StyledTableCell>
			</Fragment>
		);
	};

	const handleStoresSearch = (_event: SyntheticEvent, values: string | null) => {
		setSearchValue(values ?? "");
	};

	const sortedStores = useMemo(() => {
		const list = stores ?? [];

		return [...list].sort((a, b) => {
			const aName = normalizeText(a.storeName);
			const bName = normalizeText(b.storeName);
			return order === "asc" ? aName.localeCompare(bName) : bName.localeCompare(aName);
		});
	}, [stores, order]);

	const filteredStores = useMemo(() => {
		const query = normalizeText(searchValue?.trim() ?? "");

		if (!query) return sortedStores;

		return sortedStores.filter((store) => {
			const searchableText = [store.storeName, formatAddress(store.storeInfo)]
				.map((value) => normalizeText(value))
				.join(" ");

			return searchableText.includes(query);
		});
	}, [searchValue, sortedStores]);

	const getTableContent = () => {
		return (
			<>
				<Styles.StoresSettingsTableHeader>
					<SuggestedSearch
						ariaLabel="stores-settings-search-results"
						dataTestId="stores-settings-search-results"
						dropDownOpenOnChar={3}
						placeholder={SearchStoresInputText}
						handleSearch={(event, values) => handleStoresSearch(event, values ?? "")}
						id="stores-settings-search-results"
						searchOptions={searchOptions ?? []}
						searchValue={searchValue ?? ""}
						size="small"
					/>
				</Styles.StoresSettingsTableHeader>
				{filteredStores && filteredStores.length > 0 ? (
					<StyledTableWrapper>
						<SortableTableHead
							order={order}
							orderBy={orderBy}
							onRequestSort={handleRequestSort}
							headCells={filteredHeadCells}
						/>
						<TableBody>
							{filteredStores.map((store: models["StoreSettingsPageViewModel"]) => (
								<Styles.StoreTableRow
									key={store.storeId}
									data-testid={`store-settings-${store.storeId}`}
									onClick={() =>
										navigate(`${store.storeId}`, {
											state: {
												storeName: store.storeName
											}
										})
									}
								>
									{getTableCells(store)}
								</Styles.StoreTableRow>
							))}
						</TableBody>
					</StyledTableWrapper>
				) : (
					<Styles.StoresSettingsTableNoResultsFound>
						<img
							src="/assets/NoOrdersSadBoxImage.svg"
							alt="No Results Found"
							data-testid="stores-search-no-results-found"
						/>
						<h1>{StoreSearchNoResultsFound}</h1>
						<div>
							<div>{AdminSearchNoResultsFoundSubheader}</div>
							<ul>
								<li>{AdminStoreSearchUserBullet}</li>
								<li>{AdminSearchCheckYourSpellingBullet}</li>
							</ul>
						</div>
						<div>{ContactCareForSearchHelpText}</div>
					</Styles.StoresSettingsTableNoResultsFound>
				)}
			</>
		);
	};

	return <>{isLoading ? <TableSkeleton /> : getTableContent()}</>;
};

export default StoresSettingsTable;
